export const definitionFlowModes = ['quick', 'full'] as const

export type DefinitionFlowMode = (typeof definitionFlowModes)[number]

export const definitionFlowSteps = [
	'choice',
	'basic-data',
	'documents',
	'networks',
	'benchmarks',
	'materials',
	'people',
	'breakdown',
] as const

export type DefinitionFlowStep = (typeof definitionFlowSteps)[number]

/** The two steps that exist before the project does — everything after basic data is project-scoped. */
export const definitionFlowEntrySteps = ['choice', 'basic-data'] as const

export type DefinitionFlowEntryStep = (typeof definitionFlowEntrySteps)[number]

/**
 * The steps that embed a project asset view (its table plus its create/edit dialogs) instead of a
 * form of their own. Documents belongs to both modes; the rest are the full definition's tail.
 */
export const definitionFlowAssetSteps = ['documents', 'benchmarks', 'materials', 'people'] as const

export type DefinitionFlowAssetStep = (typeof definitionFlowAssetSteps)[number]

export function isDefinitionFlowAssetStep(step: DefinitionFlowStep): step is DefinitionFlowAssetStep {
	return definitionFlowAssetSteps.some((assetStep) => assetStep === step)
}

/**
 * Each mode's step sequence — the flow's single source of order. Later definition-flow
 * tickets plug their steps in by replacing the placeholder rendering, not this registry;
 * a step listed here and unhandled by a route renders as the placeholder slot.
 */
export const definitionFlowStepsByMode = {
	quick: ['choice', 'basic-data', 'documents', 'networks', 'breakdown'],
	full: ['choice', 'basic-data', 'documents', 'networks', 'benchmarks', 'materials', 'people', 'breakdown'],
} as const satisfies Record<DefinitionFlowMode, readonly DefinitionFlowStep[]>

export const definitionFlowStepLabelKeys = {
	choice: 'projects.definition.steps.choice',
	'basic-data': 'projects.definition.steps.basic_data',
	documents: 'projects.definition.steps.documents',
	networks: 'projects.definition.steps.networks',
	benchmarks: 'projects.definition.steps.benchmarks',
	materials: 'projects.definition.steps.materials',
	people: 'projects.definition.steps.people',
	breakdown: 'projects.definition.steps.breakdown',
} as const satisfies Record<DefinitionFlowStep, string>

function getStepIndex(mode: DefinitionFlowMode, step: DefinitionFlowStep): number {
	const steps: readonly DefinitionFlowStep[] = definitionFlowStepsByMode[mode]
	return steps.indexOf(step)
}

/** A step outside the mode's sequence (a hand-edited URL) lands back on the flow's start. */
export function resolveDefinitionFlowStep(mode: DefinitionFlowMode, step: DefinitionFlowStep): DefinitionFlowStep {
	return getStepIndex(mode, step) === -1 ? 'choice' : step
}

/** Basic data needs a chosen mode — without one the entry flow starts over at the choice step. */
export function resolveDefinitionEntryStep(
	mode: DefinitionFlowMode | undefined,
	step: DefinitionFlowEntryStep,
): DefinitionFlowEntryStep {
	return mode ? step : 'choice'
}

export function getNextDefinitionFlowStep(
	mode: DefinitionFlowMode,
	step: DefinitionFlowStep,
): DefinitionFlowStep | undefined {
	const index = getStepIndex(mode, step)
	return index === -1 ? undefined : definitionFlowStepsByMode[mode][index + 1]
}

export function getPreviousDefinitionFlowStep(
	mode: DefinitionFlowMode,
	step: DefinitionFlowStep,
): DefinitionFlowStep | undefined {
	const index = getStepIndex(mode, step)
	return index <= 0 ? undefined : definitionFlowStepsByMode[mode][index - 1]
}

export function getDefinitionFlowProgress(
	mode: DefinitionFlowMode,
	step: DefinitionFlowStep,
): { current: number; total: number } {
	const resolvedStep = resolveDefinitionFlowStep(mode, step)

	return {
		current: getStepIndex(mode, resolvedStep) + 1,
		total: definitionFlowStepsByMode[mode].length,
	}
}
